import { createIsomorphicFn } from "@tanstack/react-start"
import { getCookie } from "@tanstack/react-start/server"
import {
  DASHBOARD_APPEARANCE_COOKIE,
  type DashboardAppearance,
  dashboardAppearanceOrDefault,
  type ResolvedDashboardAppearance,
  readDashboardAppearanceCookie,
} from "./dashboardAppearance"

/**
 * Reading and resolving the appearance, for whoever renders the document.
 *
 * Separate from `dashboardAppearance.ts` because that module is pure data and
 * string handling, importable from anywhere; this one reaches for the request,
 * so it belongs to the rendering runtime. Both documents need it: the site's
 * root route serves `/login` and `/dashboard/*`, and the Contract Maker
 * deployment has a root of its own that the site cannot reach.
 *
 * `.server()` and its server-only import are removed from the client bundle by
 * the Start plugin, which discards the branch and then runs dead-code
 * elimination over the result — verified against the built client entry, which
 * contains no `async_hooks` and no `AsyncLocalStorage`.
 */
export const readDashboardAppearance = createIsomorphicFn()
  .server(
    (): DashboardAppearance =>
      // Synchronous: `getCookie` reads the request this render is already for.
      dashboardAppearanceOrDefault(getCookie(DASHBOARD_APPEARANCE_COOKIE))
  )
  .client((): DashboardAppearance => readDashboardAppearanceCookie(document.cookie))

/**
 * Resolves a preference to the look that should actually be rendered.
 *
 * Isomorphic on purpose, and that is the whole point of it. React owns
 * `<html className>` and re-renders the root on every navigation, so if this
 * answered differently from the appearance provider the two would fight: the
 * provider would apply the right class, and React's next render would
 * overwrite it from a stale value with no effect left to correct it. Making
 * both sides compute the same function of the same cookie removes that class
 * of bug rather than racing it.
 *
 * The server genuinely cannot know a device's colour scheme —
 * `Sec-CH-Prefers-Color-Scheme` is not sent unless requested, and requesting
 * it costs a round trip — so it renders "system" as dark, and the client
 * corrects it on hydration if the device disagrees. That correction reaches
 * only people who chose "system" *and* are on a light device; explicit light
 * and explicit dark, which is everyone by default, never flash.
 */
export const resolveAppearance = createIsomorphicFn()
  .server(
    (preference: DashboardAppearance): ResolvedDashboardAppearance =>
      preference === "light" ? "light" : "dark"
  )
  .client((preference: DashboardAppearance): ResolvedDashboardAppearance => {
    if (preference !== "system") return preference
    if (typeof window.matchMedia !== "function") return "dark"
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  })
